<template>
  <div class="lead-list">
    <LeadTable
      :call-targets="callTargets"
      :call-targets-meta="callTargetsMeta"
      enabled-to-display-lead
      lead-open-by-lead-list
      @open-set-operator-modal="openSetOperatorModal"
      @open-sf-modal="openSfModal"
      @update:selected-call-target-ids="handleSelectedCallTargetIdsUpdate"
    />
    <SfStatusModal
      v-if="isAvailableSalesforceApiFeature"
      :modal-name="sfModalName(sfRelatedLinkInfo)"
      :sf-related-link-info="sfRelatedLinkInfo"
      :sf-exec-sync-btn="false"
    />
    <BModal
      modal-name="callTargetSetting"
      full
    >
      <CallTargetSetting :custom-fields="customFields" />
    </BModal>
    <BModal
      modal-name="setOperator"
      width="380px"
      height="100px"
    >
      <SetOperator
        :current-operator-id="currentOperatorId"
        :operator-users="activeUsers"
        :needs-withdraw-option="true"
        @assign-operator="assignOperator"
        @withdraw-operator="withdrawOperator"
      />
    </BModal>
    <BModal
      modal-name="target"
      class="lead-modal"
      :delegate="leadModalDelegate"
      full
    >
      <Lead
        ref="callTarget"
        :call-target-id="currentCallTargetId"
        :call-targets="callTargets"
        :call-targets-meta="callTargetsMeta"
        @close-modal="handleCloseLeadModalAction"
      />
    </BModal>
  </div>
</template>

<script>
import { mapWaitingActions } from 'vue-wait';
import { mapGetters } from 'vuex';
import CallTargetSetting from '@/components/organisms/user/CallTargetSetting.vue';
import SfStatusModal from '@/components/organisms/user/general/modal/salesforce/SfStatusModal.vue';
import Lead from '@/components/organisms/user/lead/Lead.vue';
import LeadTable from '@/components/organisms/user/leadList/LeadTable.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useLeadModalClose } from '@/composable/user/leadList/lead-modal-close';
import { useUsers } from '@/composable/user/user/users';

export default {
  components: {
    SfStatusModal,
    Lead,
    CallTargetSetting,
    LeadTable,
  },
  props: {
    callTargets: {
      type: Array,
      required: true,
    },
    callTargetsMeta: {
      type: Object,
      required: true,
    },
    customFields: {
      type: Array,
      required: true,
    },
  },
  emits: [
    'update:selectedCallTargetIds',
  ],
  setup() {
    const { isAvailableSalesforceApiFeature } = useAvailableFeatureCheck();
    const { activeUsers } = useUsers();
    // NOTE: LeadModalを閉じる前の確認ダイアログは、この中で登録するルートガードが単一の地点で行う
    const { callTargetIdInUrl, requestLeadModalClose } = useLeadModalClose();
    return {
      isAvailableSalesforceApiFeature,
      activeUsers,
      callTargetIdInUrl,
      requestLeadModalClose,
    };
  },
  data () {
    return {
      selectedOperatorCallTargetId: null,
      currentOperatorId: null,
      sfRelatedLinkInfo: {},
      leadModalDelegate: {
        // NOTE: BModalはshouldCloseがtrueのときだけ自分でhideする。リードモーダルの開閉は
        // URLのcallTargetIdを唯一の情報源とするため、ここでは直接閉じずに「URLから外す要求」
        // だけ出して常にfalseを返す。確認とhideはURL変更→ルートガード→watchの経路が行う。
        shouldClose: () => {
          this.requestLeadModalClose();
          return false;
        },
        beforeClose: this.beforeLeadModalClose,
      },
    };
  },
  computed: {
    ...mapGetters('userUi', [
      'currentCallTargetId',
    ]),
    ...mapGetters('user', [
      'currentUser',
    ]),
  },
  watch: {
    callTargetIdInUrl (callTargetId) {
      this.syncLeadModalWithUrl(callTargetId);
    },
  },
  mounted () {
    this.syncLeadModalWithUrl(this.callTargetIdInUrl);
  },
  methods: {
    ...mapWaitingActions('userUi', {
      setTargetMenuVisibleResetAction: 'setTargetMenuVisibleResetWait',
      setTargetHeaderMoveButtonDisabledAction: 'setTargetHeaderMoveButtonDisabledWait',
      setSelectedCallResultAction: 'setSelectedCallResultWait',
      setSelectedOpportunityAction: 'setSelectedOpportunityWait',
      setSelectedNextActionAction: 'setSelectedNextActionWait',
      setSelectedSequenceInstanceIdAction: 'setSelectedSequenceInstanceIdWait',
      setCurrentCallTargetIdAction: 'setCurrentCallTargetIdActionWait',
    }),
    ...mapWaitingActions('user', {
      createOperatorAction: 'createOperatorWait',
      withdrawOperatorAction: 'withdrawOperatorWait',
      getCallTargetsBackgroundAction: { action: 'getCallTargetsAction', loader: 'getCallTargetsBackgroundWait' },
    }),
    syncLeadModalWithUrl (callTargetId) {
      if (callTargetId == null) {
        // NOTE: ブラウザバックでモーダルがstate上開いたままとなるので、不要なときは明確に閉じる
        this.$modal.hide('target');
        return;
      }
      this.setCurrentCallTargetIdAction(callTargetId);
      this.$modal.show('target');
    },
    /**
     * NOTE: モーダルを閉じる要求はURLの変更だけを行う。確認ダイアログはルートガードが出し、
     * 確定後にcallTargetIdInUrlのwatchがsyncLeadModalWithUrl経由でhideまで運ぶ。
     */
    async handleCloseLeadModalAction() {
      await this.requestLeadModalClose();
    },
    beforeLeadModalClose() {
      // NOTE: URLの変更が閉じる起点になっているため、ここでURLを触ってはいけない。
      // 触ると二重にナビゲーションが走り、確認ダイアログも2回出る。
      this.setTargetMenuVisibleResetAction();
      this.setSelectedCallResultAction({});
      this.setSelectedOpportunityAction({});
      this.setSelectedNextActionAction({});
      this.setSelectedSequenceInstanceIdAction(null);
      this.$refs.callTarget?.closeWidget();
      this.setTargetHeaderMoveButtonDisabledAction(false);
    },
    openSetOperatorModal (callTargetId, operatorId) {
      this.selectedOperatorCallTargetId = callTargetId;
      this.currentOperatorId = operatorId;
      this.$modal.show('setOperator');
    },
    sfModalName(sfRelatedLinkInfo) {
      return `sfStatusListModal${sfRelatedLinkInfo.id}`;
    },
    openSfModal(sfRelatedLinkInfo) {
      this.sfRelatedLinkInfo = sfRelatedLinkInfo;
      this.$modal.show(this.sfModalName(sfRelatedLinkInfo));
    },
    async assignOperator (selectedOperatorId) {
      try {
        await this.createOperatorAction({
          body: {
            operatorId: selectedOperatorId,
            callTargetId: this.selectedOperatorCallTargetId,
          },
        });
        this.$toast.show(this.$t('general.done.of', { action: this.$t('operatorAssign.of', { item: this.$t('home.member') }) }));
      } catch (e) {
        this.$toast.show({
          message: this.$t('general.fail.to', { action: this.$t('operatorAssign.assign') }),
          type: 'error',
        });
      }
      this.getCallList();
    },
    async withdrawOperator () {
      try {
        await this.withdrawOperatorAction(this.selectedOperatorCallTargetId);
        this.$toast.show(this.$t('general.done.of', { action: `${this.$t('operatorAssign.assign')}${this.$t('general.withdraw.text')}` }));
      } catch (e) {
        this.$toast.show({
          message: this.$t('general.fail.to', { action: `${this.$t('operatorAssign.assign')}${this.$t('general.withdraw.text')}` }),
          type: 'error',
        });
      }
      this.getCallList();
    },
    async getCallList () {
      // UX改善のため、データ再読込時に全体ローディングしないようになっている
      // organismsでの処理は、必ず初期データがある前提なので、backgroundのみにしている
      // TODO: データ更新のタイミングなど戦略を決める
      this.$toast.show({ message: this.$t('callList.update.loading'), type: 'info' });
      await this.getCallTargetsBackgroundAction();
      this.$toast.show({ message: this.$t('callList.update.done'), type: 'success' });
    },
    handleSelectedCallTargetIdsUpdate(selectedCallTargetIds) {
      this.$emit('update:selectedCallTargetIds', selectedCallTargetIds);
    },
  },
};
</script>

<style lang="scss" scoped>
  .lead-modal {
    z-index: 11 !important; // Modalコンポーネントの1つ上を指定
  }
</style>
